// src/components/ui/InputField.js
// Enhanced InputField with optional validation messages and password toggle

import React, { useState, useEffect } from 'react';
import { Eye, EyeClosed } from 'lucide-react';

const InputField = ({
  label,
  value,
  onChange,
  onFocus,
  onBlur,
  placeholder = '',
  type = 'text',
  required = false,
  error = '',
  id,
  maxWidth = '320px',
  height = '32px',
  
  // Validation message props
  validationMessage = '', // The validation message text
  validationMode = null, // 'permanent', 'inline', or null (no validation)
  validationCheck = 'blur', // 'live' or 'blur'
  isValid = true, // Whether current value passes validation
  
  // Password toggle props
  showPasswordToggle = false, // Enable password show/hide toggle
  
  ...props
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [touched, setTouched] = useState(false);

  // Determine if we should show validation error
  const showValidationError = validationMessage && !isValid && (
    validationCheck === 'live' || touched
  );

  // Determine validation message color
  const validationColor = showValidationError ? 'text-red-600' : 'text-app-gray-500';
  
  // Determine if validation message should be visible
  const showValidationText = validationMessage && (
    validationMode === 'permanent' || showValidationError
  );

  const handleFocus = (e) => {
    setIsFocused(true);
    if (onFocus) onFocus(e);
  };

  const handleBlur = (e) => {
    setIsFocused(false);
    setTouched(true);
    if (onBlur) onBlur(e);
  };

  // Reset touched state when validation becomes valid (for live clearing)
  useEffect(() => {
    if (touched && isValid && validationCheck === 'blur') {
      // Don't reset touched, but validation will clear because isValid is true
    }
  }, [isValid, touched, validationCheck]);

  // Determine actual input type (handle password toggle)
  const inputType = showPasswordToggle && type === 'password' 
    ? (showPassword ? 'text' : 'password')
    : type;

  // Calculate padding for password toggle button
  const inputPaddingRight = showPasswordToggle ? '40px' : undefined;

  // Detect if input is disabled
  const isDisabled = props.disabled;

  return (
    <div className="flex flex-col">
      {/* Label */}
      {label && (
        <label
          htmlFor={id}
          className="text-app-xs text-app-gray-600 font-medium mb-app-md"
        >
          {label}
          {required && <span className="text-red-600 ml-1">*</span>}
        </label>
      )}

      {/* Input Container - flexible width with max-width constraint */}
      <div
        className={`relative rounded-full transition-all duration-app-normal w-full ring ring-app-gray-900 ${
          isFocused && !isDisabled ? 'ring-2' : 'ring-1'
        } ${isDisabled ? 'bg-app-gray-200' : 'bg-app-white'}`}
        style={{
          maxWidth: maxWidth,
          height: height,
        }}
        onMouseEnter={() => !isDisabled && setIsHovered(true)}
        onMouseLeave={() => !isDisabled && setIsHovered(false)}
      >
        {/* Background layer - creates the inset gray pill effect */}
        {/* KEY FIX: Added z-0 so this layer sits BELOW the container's ring */}
        <div
          className={`
            absolute rounded-full bg-app-gray-300 z-0
            ${isFocused && !isDisabled ? 'transition-all duration-app-normal' : ''}
            ${!isDisabled && isFocused
              ? 'inset-0'
              : !isDisabled && isHovered
                ? 'inset-[3px]'
                : 'inset-0 opacity-0'
            }
          `}
        />
        
        {/* Input Element - always maintains same position with fixed padding */}
        <input
          id={id}
          type={inputType}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          onFocus={handleFocus}
          onBlur={handleBlur}
          className={`
            relative z-10 w-full h-full rounded-full px-app-lg 
            bg-transparent text-app-sm text-app-gray-900 font-normal 
            placeholder:font-normal outline-none border-none 
            transition-colors duration-app-normal
            ${isHovered || isFocused ? 'placeholder:text-app-gray-500' : 'placeholder:text-app-gray-400'}
          `}
          style={{
            WebkitBoxShadow: '0 0 0 1000px transparent inset',
            WebkitTextFillColor: 'inherit',
            paddingRight: inputPaddingRight,
          }}
          {...props}
        />

        {/* Password Toggle Button */}
        {showPasswordToggle && (
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-app-md top-1/2 -translate-y-1/2 text-app-gray-500 hover:text-app-gray-900 transition-colors z-20 flex items-center"
            tabIndex={-1}
            aria-label={showPassword ? "Hide password" : "Show password"}
          >
            {showPassword ? <EyeClosed size={18} /> : <Eye size={18} />}
          </button>
        )}
      </div>

      {/* Validation Message Container - Fixed height to prevent layout shift */}
      <div 
        className="mt-app-xs ml-app-lg"
        style={{ minHeight: validationMessage ? '16px' : '0' }}
      >
        {showValidationText && (
          <p className={`text-app-2xs ${validationColor} transition-colors duration-app-fast`}>
            {validationMessage}
          </p>
        )}
      </div>

      {/* Legacy Error message - kept for backwards compatibility */}
      {error && (
        <p className="text-red-600 text-app-sm mt-app-xs ml-app-lg">
          {error}
        </p>
      )}
      
      <style jsx>{`
        input:-webkit-autofill,
        input:-webkit-autofill:hover,
        input:-webkit-autofill:focus,
        input:-webkit-autofill:active {
          -webkit-box-shadow: 0 0 0 1000px transparent inset !important;
          -webkit-text-fill-color: inherit !important;
          font-size: 0.875rem !important;
          transition: background-color 5000s ease-in-out 0s;
        }
      `}</style>
    </div>
  );
};

export default InputField;